import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { useEntidadeContext } from '../../EntidadeContext';
import { useAlertModal } from '../../AlertModalContext';

/* ── base ── */
const inputBase = {
  width: '100%', padding: '9px 12px', borderRadius: '8px',
  border: '1px solid #e5e7eb', fontSize: `calc(13px * var(--font-scale))`, color: '#374151',
  fontFamily: 'inherit', outline: 'none', backgroundColor: '#fff',
  boxSizing: 'border-box', transition: 'border-color .15s, box-shadow .15s',
};
const fh = {
  onFocus: e => { e.target.style.borderColor = '#2563eb'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,.1)'; },
  onBlur:  e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; },
};
const Label = ({ children }) => (
  <label style={{ fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', display: 'block', marginBottom: '5px' }}>
    {children}
  </label>
);
const PrimaryBtn = ({ children, onClick, disabled, loading, color = '#2563eb', small, outline, danger }) => {
  const bg = danger ? '#dc2626' : color;
  return (
    <button
      type="button" onClick={onClick} disabled={disabled || loading}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        padding: small ? '5px 12px' : '9px 20px', borderRadius: '8px',
        border: outline ? `1px solid ${bg}` : 'none',
        background: outline ? 'transparent' : ((disabled || loading) ? '#93c5fd' : `linear-gradient(135deg,#1e3a5f,${bg})`),
        color: outline ? bg : '#fff',
        fontSize: small ? '12px' : '13px', fontWeight: '600', fontFamily: 'inherit',
        cursor: (disabled || loading) ? 'not-allowed' : 'pointer',
      }}
    >
      {loading && <Spinner as="span" animation="border" size="sm" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />}
      {children}
    </button>
  );
};

const TagsInteresseTab = () => {
  const { currentEntidade } = useEntidadeContext();
  const { showAlertModal }  = useAlertModal();

  const [tags, setTags]         = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError]       = useState(null);
  const [showModal, setShowModal]   = useState(false);
  const [isSaving, setIsSaving]     = useState(false);
  const [currentTag, setCurrentTag] = useState(null);
  const [tagData, setTagData]       = useState({ nome: '', descricao: '' });

  const fetchTags = useCallback(async () => {
    if (!currentEntidade) return;
    setIsLoading(true); setError(null);
    try {
      const response = await axios.get(`/api/interesses?entidade_id=${currentEntidade.id}`);
      setTags(response.data.interesses || []);
    } catch {
      setError('Não foi possível carregar as tags de interesse.');
      setTags([]);
    } finally { setIsLoading(false); }
  }, [currentEntidade]);

  useEffect(() => { fetchTags(); }, [fetchTags]);

  const handleOpenModal = (tag = null) => {
    setCurrentTag(tag);
    setTagData(tag ? { nome: tag.nome, descricao: tag.descricao || '' } : { nome: '', descricao: '' });
    setShowModal(true);
  };
  const handleCloseModal = () => { setShowModal(false); setCurrentTag(null); setTagData({ nome: '', descricao: '' }); };
  const handleInputChange = (e) => { const { name, value } = e.target; setTagData(prev => ({ ...prev, [name]: value })); };

  const handleSaveTag = async () => {
    if (!tagData.nome.trim()) { showAlertModal('Atenção', 'O nome da tag não pode ser vazio.', 'warning'); return; }
    if (!currentEntidade) { showAlertModal('Erro', 'Nenhuma entidade selecionada.', 'danger'); return; }
    setIsSaving(true);
    try {
      if (currentTag?.id) {
        const response = await axios.put(`/api/interesses/${currentTag.id}`, tagData);
        const updatedTag = response.data.interesse;
        setTags(prev => prev.map(t => t.id === updatedTag.id ? updatedTag : t));
        showAlertModal('Sucesso', 'Tag atualizada com sucesso!', 'success');
      } else {
        const response = await axios.post('/api/interesses', { ...tagData, entidade_id: currentEntidade.id });
        const newTag = response.data.interesse;
        setTags(prev => [...prev, newTag].sort((a, b) => a.nome.localeCompare(b.nome)));
        showAlertModal('Sucesso', 'Tag criada com sucesso!', 'success');
      }
      handleCloseModal();
    } catch (err) {
      showAlertModal('Erro', `Falha ao salvar a tag: ${err.response?.data?.message || err.message}`, 'danger');
    } finally { setIsSaving(false); }
  };

  const handleDeleteTag = async (tagId) => {
    if (window.confirm('Tem certeza que deseja excluir esta tag? A exclusão também removerá o vínculo desta tag de todos os pacientes.')) {
      try {
        await axios.delete(`/api/interesses/${tagId}`);
        setTags(prev => prev.filter(t => t.id !== tagId));
        showAlertModal('Sucesso', 'Tag excluída com sucesso!', 'success');
      } catch (err) {
        showAlertModal('Erro', `Falha ao excluir a tag: ${err.response?.data?.message || err.message}`, 'danger');
      }
    }
  };

  return (
    <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        .tag-row:hover { background: #f0f9ff !important; }
        .tm-modal .modal-content { border-radius: 16px !important; border: none !important; overflow: hidden; font-family: 'Inter','Segoe UI',sans-serif; box-shadow: 0 25px 60px rgba(0,0,0,.18) !important; }
        .tm-modal .modal-header { background: linear-gradient(135deg,#1e3a5f,#2563eb); border: none !important; padding: 18px 22px !important; }
        .tm-modal .modal-body { padding: 22px !important; background: #f8fafc; }
        .tm-modal .modal-footer { background: #f1f5f9; border-top: 1px solid #e5e7eb !important; padding: 12px 22px !important; }
        .tm-modal .btn-close { filter: invert(1) brightness(2) !important; }
      `}</style>

      {/* ── HEADER ── */}
      <div style={{ background: 'linear-gradient(135deg,#1e3a5f,#2563eb)', borderRadius: '14px', padding: '20px 24px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: '12px', backgroundColor: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="22" height="22" fill="none" stroke="#fff" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
            </svg>
          </div>
          <div>
            <div style={{ fontSize: '16px', fontWeight: '800', color: '#fff' }}>Tags de Interesse</div>
            <div style={{ fontSize: `calc(12px * var(--font-scale))`, color: 'rgba(255,255,255,.7)' }}>{tags.length} tag{tags.length !== 1 ? 's' : ''} cadastrada{tags.length !== 1 ? 's' : ''}</div>
          </div>
        </div>
        <PrimaryBtn onClick={() => handleOpenModal()} color="#fff" outline>
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
          Nova Tag
        </PrimaryBtn>
      </div>

      {/* ── INFO CARD ── */}
      <div style={{ backgroundColor: '#eff6ff', borderRadius: '12px', border: '1px solid #bfdbfe', padding: '14px 18px', marginBottom: '20px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
        <svg width="18" height="18" fill="none" stroke="#2563eb" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: '1px' }}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div>
          <div style={{ fontSize: `calc(12px * var(--font-scale))`, fontWeight: '700', color: '#1e40af', marginBottom: '3px' }}>Para que servem as Tags de Interesse?</div>
          <div style={{ fontSize: `calc(12px * var(--font-scale))`, color: '#3b82f6', lineHeight: '1.5' }}>
            As tags ajudam a segmentar pacientes para ações de marketing. Ex: crie a tag <strong>"Botox"</strong> e vincule a pacientes interessados. Futuramente, envie promoções específicas para este grupo.
          </div>
        </div>
      </div>

      {/* ── ERRO ── */}
      {error && (
        <div style={{ padding: '12px 16px', borderRadius: '10px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: `calc(13px * var(--font-scale))`, marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {/* ── LOADING ── */}
      {isLoading && !showModal ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 0', gap: '12px' }}>
          <Spinner animation="border" variant="primary" style={{ width: '28px', height: '28px' }} />
          <span style={{ fontSize: `calc(13px * var(--font-scale))`, color: '#6b7280' }}>Carregando tags...</span>
        </div>
      ) : (
        /* ── TABELA ── */
        <div style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          {/* Cabeçalho */}
          <div style={{ display: 'grid', gridTemplateColumns: '60px 1fr 2fr 120px', background: 'linear-gradient(135deg,#f8fafc,#f1f5f9)', borderBottom: '2px solid #e2e8f0', padding: '10px 16px' }}>
            {['#', 'Nome', 'Descrição', 'Ações'].map((h, i) => (
              <div key={i} style={{ fontSize: `calc(10px * var(--font-scale))`, fontWeight: '800', color: '#64748b', textTransform: 'uppercase', letterSpacing: '.5px', textAlign: i === 3 ? 'center' : 'left' }}>
                {h}
              </div>
            ))}
          </div>

          {/* Linhas */}
          {tags.length === 0 ? (
            <div style={{ padding: '48px 24px', textAlign: 'center' }}>
              <svg width="40" height="40" fill="none" stroke="#d1d5db" viewBox="0 0 24 24" style={{ margin: '0 auto 12px', display: 'block' }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
              </svg>
              <div style={{ fontSize: `calc(14px * var(--font-scale))`, fontWeight: '600', color: '#9ca3af' }}>Nenhuma tag cadastrada</div>
              <div style={{ fontSize: `calc(12px * var(--font-scale))`, color: '#d1d5db', marginTop: '4px' }}>Clique em "Nova Tag" para começar</div>
            </div>
          ) : tags.map((tag, idx) => (
            <div
              key={tag.id}
              className="tag-row"
              style={{
                display: 'grid', gridTemplateColumns: '60px 1fr 2fr 120px',
                padding: '12px 16px', borderBottom: idx < tags.length - 1 ? '1px solid #f1f5f9' : 'none',
                alignItems: 'center', transition: 'background .15s',
              }}
            >
              <div style={{ fontSize: `calc(12px * var(--font-scale))`, color: '#9ca3af', fontWeight: '600' }}>#{tag.id}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#2563eb', flexShrink: 0 }} />
                <span style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '700', color: '#111827' }}>{tag.nome}</span>
              </div>
              <div style={{ fontSize: `calc(13px * var(--font-scale))`, color: '#6b7280', paddingRight: '16px' }}>{tag.descricao || <span style={{ color: '#d1d5db', fontStyle: 'italic' }}>Sem descrição</span>}</div>
              <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                <button
                  type="button" onClick={() => handleOpenModal(tag)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '5px 10px', borderRadius: '7px', border: '1px solid #bfdbfe', backgroundColor: '#eff6ff', color: '#2563eb', fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Editar
                </button>
                <button
                  type="button" onClick={() => handleDeleteTag(tag.id)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '5px 10px', borderRadius: '7px', border: '1px solid #fecaca', backgroundColor: '#fef2f2', color: '#dc2626', fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  <svg width="11" height="11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Excluir
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ══════════════════════════════════
          MODAL ADICIONAR / EDITAR
      ══════════════════════════════════ */}
      <Modal show={showModal} onHide={handleCloseModal} centered dialogClassName="tm-modal">
        <Modal.Header closeButton>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '34px', height: '34px', borderRadius: '9px', backgroundColor: 'rgba(255,255,255,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="16" height="16" fill="none" stroke="#fff" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
              </svg>
            </div>
            <div>
              <div style={{ fontSize: `calc(14px * var(--font-scale))`, fontWeight: '700', color: '#fff' }}>{currentTag ? 'Editar Tag' : 'Nova Tag de Interesse'}</div>
              <div style={{ fontSize: `calc(11px * var(--font-scale))`, color: 'rgba(255,255,255,.7)' }}>Preencha os dados abaixo</div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          <div style={{ marginBottom: '16px' }}>
            <Label>Nome da Tag</Label>
            <input
              style={inputBase} type="text" name="nome"
              value={tagData.nome} onChange={handleInputChange}
              placeholder="Ex: Botox" autoFocus {...fh}
            />
          </div>
          <div>
            <Label>Descrição</Label>
            <textarea
              style={{ ...inputBase, resize: 'vertical', minHeight: '90px' }}
              name="descricao" value={tagData.descricao} onChange={handleInputChange}
              placeholder="Para que serve esta tag? Ex: Pacientes com interesse em toxina botulínica."
              {...fh}
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button" onClick={handleCloseModal} disabled={isSaving}
            style={{ padding: '8px 18px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', cursor: isSaving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}
          >
            Cancelar
          </button>
          <PrimaryBtn onClick={handleSaveTag} disabled={isSaving} loading={isSaving} color="#2563eb">
            <svg width="13" height="13" fill="none" stroke="#fff" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
            {isSaving ? 'Salvando...' : currentTag ? 'Atualizar Tag' : 'Criar Tag'}
          </PrimaryBtn>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TagsInteresseTab;
